import React, { useCallback } from "react";
import { useProject } from "../../App/state";
import { useNotification } from "../../contexts/Notifications";
import {
  useFloorplanByIdQuery,
  useSpaceBimByIdQuery,
  useUpdateSpaceBimsMutation,
} from "../../generated/types";
import ErrorPage from "../../pages/ErrorPage";
import LoadingPage from "../../pages/LoadingPage";
import { EditPropertiesModal } from "./EditPropertiesModal";
import {
  BimFloorModel,
  FloorplanEditPropertiesInput,
} from "./hooks/useFloorplan";

interface EditPropertiesProps {
  floorplanId: string;
  onEdit: (floorplanData: FloorplanEditPropertiesInput) => Promise<void>;
  onClose: () => void;
  spaceName: string;
  spaceId?: string;
  bimMode?: boolean;
}

export const EditProperties = (props: EditPropertiesProps) => {
  const {
    floorplanId,
    spaceName,
    onClose,
    onEdit,
    spaceId,
    bimMode = false,
  } = props;
  const [uploadInProgress, setUploadInProgress] = React.useState(false);
  const tenant = useProject();
  const notify = useNotification();

  // Add the mutation hook
  const [updateSpaceBims] = useUpdateSpaceBimsMutation({
    onCompleted: () => {
      notify("Space BIM models updated successfully!", "success");
    },
    onError: (error) => {
      notify(`Failed to update space BIM models: ${error.message}`, "error");
    },
  });

  const uploadFloorplanCallback = useCallback(
    async (editedFloorplan: Omit<FloorplanEditPropertiesInput, "id">) => {
      setUploadInProgress(true);
      onEdit({
        ...editedFloorplan,
        id: floorplanId,
      });
      setUploadInProgress(false);
      onClose();
    },
    [floorplanId, onEdit, onClose],
  );

  const uploadSpaceBimPropertiesCallback = useCallback(
    async (bimFloorModels: BimFloorModel[]) => {
      if (!spaceId) {
        notify("No space ID provided", "error");
        return;
      }

      setUploadInProgress(true);

      try {
        const bimModels = bimFloorModels.map((model) => ({
          label: model.label,
          bimModelId: model.bimFloorId,
          isCommon: model.isCommon ?? true,
        }));

        await updateSpaceBims({
          variables: {
            tenant,
            spaceId,
            bimModels,
          },
        });

        onClose();
      } catch (error) {
        console.error("Error updating space BIM models:", error);
      } finally {
        setUploadInProgress(false);
      }
    },
    [spaceId, tenant, updateSpaceBims, notify, onClose],
  );

  const { data, loading, error } = useFloorplanByIdQuery({
    variables: { tenant, id: floorplanId },
    skip: !floorplanId,
  });

  const {
    data: spaceBimData,
    loading: spaceBimLoading,
    error: spaceBimError,
  } = useSpaceBimByIdQuery({
    variables: { tenant, id: spaceId! },
    skip: !spaceId || !bimMode,
  });

  if (loading || spaceBimLoading) {
    return <LoadingPage />;
  }

  if (error || spaceBimError) {
    return <ErrorPage error={error || spaceBimError!} />;
  }

  const floorplan = data?.floorplans[0];

  const spaceBimModels: BimFloorModel[] =
    spaceBimData?.space?.bimModels?.map((model) => ({
      label: model.label,
      bimFloorId: model.bimModelId,
      isCommon: model.isCommon ?? true,
    })) ?? [];

  if (!floorplan && !bimMode) {
    return (
      <ErrorPage
        error={new Error(`Could not find floorplan with id ${floorplanId}`)}
      />
    );
  }

  return (
    <EditPropertiesModal
      floorplan={floorplan ?? undefined}
      spaceBimModels={spaceBimModels}
      uploadInProgress={uploadInProgress}
      onClose={onClose}
      spaceName={spaceName}
      editFloorplanProperties={uploadFloorplanCallback}
      editSpaceBimProperties={uploadSpaceBimPropertiesCallback}
      bimMode={bimMode}
    />
  );
};
